import { usePathname, useSearchParams, useRouter as useReactRouter } from 'next/navigation';
import { useRouter } from '@/lib/router-events/patch-router/router';
import type { Filter } from '@cvent/venue-search-shared/types';
import { useCallback } from 'react';
import { queryStringConstants } from '@/constants/queryConstants';
import type { Params } from '@/utils/urlHelper';
import { prepareUrl, removeHubPageInUrl, updateSearchLocationInUrl } from '@/utils/urlHelper';
import { Categories } from '@/constants/csnSearchStatsConstants';
import { useAppFeatures } from './useAppFeatures';

const spaceFilters = [
  'PRICE_CATEGORY',
  'TOTAL_MEETING_SPACE',
  'EXHIBIT_SPACE',
  'LARGEST_ROOM',
  'SECOND_LARGEST_ROOM',
  'CEILING_HEIGHT',
  'CAPACITY_SEATED',
  'CAPACITY_STANDING',
  'NUMBER_OF_MEETING_ROOMS',
  'NUMBER_OF_SLEEPING_ROOMS'
];

const listFilters = ['PLANNER_TAGS', 'AMENITIES', 'VENUE_DETAILS'];

/**
 * React hook to apply filter & trigger search
 */
export const useApplyFilter = () => {
  const { isHypheniseUrlEnabled } = useAppFeatures();
  const router = useRouter();

  // Used for no-spinner routing
  const reactRouter = useReactRouter();
  const searchParams = useSearchParams();
  const path = usePathname();

  const params = new URLSearchParams(searchParams === null ? undefined : searchParams);
  // Reset following query params
  params.delete('p');

  const applyFilters = useCallback(
    async (
      term: string | null,
      filters: Filter,
      isSearchTermEncoded: boolean,
      sortType?: string,
      geoCode?: string,
      isGeoMarkedForRemoval?: boolean,
      isHubPageMarkedForRemoval?: boolean,
      isPrevGeoInfoNeeded?: boolean
    ) => {
      let url = path;
      if (!isPrevGeoInfoNeeded) {
        params.delete(queryStringConstants.LAT);
        params.delete(queryStringConstants.LNG);
        params.delete(queryStringConstants.PREV_GEO);
      }
      if (!isSearchTermEncoded && term) {
        url = updateSearchLocationInUrl(term, path, isHypheniseUrlEnabled);
      }

      if (isGeoMarkedForRemoval) {
        params.delete(queryStringConstants.GEO);
      }

      if (isHubPageMarkedForRemoval) {
        // remove hub page from path params
        url = removeHubPageInUrl(path);
      }

      // Remove Empty filters
      spaceFilters.forEach(s => {
        if (filters[s] && !filters[s].minimum && !filters[s].maximum) {
          // eslint-disable-next-line no-param-reassign
          delete filters[s];
        }
      });
      listFilters.forEach(s => {
        if (filters[s] && filters[s].length === 0) {
          // eslint-disable-next-line no-param-reassign
          delete filters[s];
        }
      });
      // Removed MULTI_FILTER
      if (
        filters.MULTI_FILTER &&
        (!filters.MULTI_FILTER.CHAIN || filters.MULTI_FILTER.CHAIN.length === 0) &&
        (!filters.MULTI_FILTER.BRAND || filters.MULTI_FILTER.BRAND.length === 0) &&
        (!filters.MULTI_FILTER.VENUE_NAME || filters.MULTI_FILTER.VENUE_NAME.length === 0)
      ) {
        // eslint-disable-next-line no-param-reassign
        delete filters.MULTI_FILTER;
      }

      const result = await fetch('/venues/api/filter', {
        method: 'POST',
        body: JSON.stringify(filters)
      });
      if (result.ok) {
        const data = await result.json();
        const additionalParams = [
          { key: 'filter', value: data.encoded },
          ...(sortType ? [{ key: 'sort', value: sortType || 'CVENT_PICKS' }] : []),
          ...(geoCode ? [{ key: 'geo', value: geoCode }] : [])
        ];
        router.push(prepareUrl(url, params, ...additionalParams));
      }
    },
    [params]
  );

  const applyHubPagesNavigation = useCallback(
    (hubPath: string) => {
      const hubParams = new URLSearchParams(searchParams === null ? undefined : searchParams);
      hubParams.delete('p');
      hubParams.delete('filter');

      // Append hub pages path
      const segments = path.split('/');
      const hubCategory = segments.find(
        segment => Array.from(Categories.values()).filter(f => f.path === segment).length > 0
      );
      if (hubCategory) {
        return prepareUrl(path.replace(hubCategory, hubPath), hubParams);
      }
      return prepareUrl(`${path}/${hubPath}`, hubParams);
    },
    [params]
  );

  const applyPrevFiltersWithUpdatedLocation = useCallback(
    async (geo: string, lat: number | string | null, lng: number | string | null, prevGeo: string | null) => {
      const paramList: Params[] = [{ key: queryStringConstants.GEO, value: geo }];
      if (lat) {
        paramList.push({ key: queryStringConstants.LAT, value: lat.toString() });
      }
      if (lng) {
        paramList.push({ key: queryStringConstants.LNG, value: lng.toString() });
      }
      if (prevGeo) {
        paramList.push({ key: queryStringConstants.PREV_GEO, value: prevGeo });
      }
      reactRouter.push(prepareUrl(path, params, ...paramList));
    },
    [params, path, reactRouter]
  );

  return { applyFilters, applyHubPagesNavigation, applyPrevFiltersWithUpdatedLocation };
};
